import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

export default function P78PilotPath({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />双通道流程
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            常规自动评价与<span className="text-gold-600">异常复核机制</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            规模化依靠自动流程，可信度依靠异常识别、人工复核和持续校准
          </p>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Normal Channel */}
            <div className="p-6 glass-liquid rounded-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
                  <i className="ri-settings-3-line text-lg text-primary-600" />
                </div>
                <div>
                  <h4 className="font-heading text-base font-bold text-foreground-800">常规课程</h4>
                  <p className="text-xs text-primary-600 font-body font-semibold">自动完成</p>
                </div>
              </div>
              <div className="space-y-2.5 pl-4 border-l-2 border-primary-200/40">
                {['系统自动评价', '报告生成', '进入档案和治理图'].map((step, si) => (
                  <div key={si} className="flex items-center gap-2 text-sm text-foreground-600 font-body">
                    <i className="ri-check-line text-xs text-primary-600" />
                    {step}
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-400 font-body mt-4 italic">前提：课程模型已完成专家校准，评价口径稳定</p>
            </div>

            {/* Exception Channel */}
            <div className="p-6 glass-liquid rounded-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gold-100/80 border border-gold-300/40 flex items-center justify-center">
                  <i className="ri-alert-line text-lg text-gold-600" />
                </div>
                <div>
                  <h4 className="font-heading text-base font-bold text-foreground-800">异常 / 争议 / 申诉 / 合规</h4>
                  <p className="text-xs text-gold-600 font-body font-semibold">人工复核</p>
                </div>
              </div>
              <div className="space-y-2.5 pl-4 border-l-2 border-gold-300/40">
                {['标记提示', '人工复核', '结果修订', '样本沉淀与规则完善'].map((step, si) => (
                  <div key={si} className="flex items-center gap-2 text-sm text-foreground-600 font-body">
                    <i className="ri-check-line text-xs text-gold-600" />
                    {step}
                  </div>
                ))}
              </div>
              <p className="text-xs text-foreground-400 font-body mt-4 italic">低置信度、新课程、样本不足 → 先校准再评价</p>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center mt-4 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              这种双通道设计既保障<strong className="text-gold-700">规模化效率</strong>，也保留必要的<strong className="text-gold-700">解释、校准和反馈通道</strong>。复核结果还会继续沉淀为边界案例和训练样本，反向改进后续模型。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}